const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';
const APP_KEY_STORAGE = 'bilmat:app-key';

/** Delat app-lösenord (API-grinden). localStorage kan vara blockerat — svälj då. */
export function getAppKey(): string | null {
  try {
    return window.localStorage.getItem(APP_KEY_STORAGE);
  } catch {
    return null;
  }
}

export function setAppKey(key: string): void {
  try {
    window.localStorage.setItem(APP_KEY_STORAGE, key);
  } catch {
    /* blockerat — nyckeln gäller bara denna sidladdning */
  }
}

export function clearAppKey(): void {
  try {
    window.localStorage.removeItem(APP_KEY_STORAGE);
  } catch {
    /* blockerat */
  }
}

/** Bygger en URL mot proxyn: `/api/?endpoint=…&…`. App-nyckeln läggs på automatiskt. */
export function buildApiUrl(endpoint: string, params: Record<string, string> = {}): string {
  const search = new URLSearchParams({ endpoint, ...params });
  const key = getAppKey();
  if (key && !search.has('k')) search.set('k', key);
  return `${API_BASE}/?${search.toString()}`;
}

type ApiResult<T> = { ok: true } & T;
type ApiFailure = { ok: false; error: string; [key: string]: unknown };

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(message: string, code: string, status: number, details: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export async function apiGet<T>(
  endpoint: string,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<ApiResult<T>> {
  const url = buildApiUrl(endpoint, params);

  const response = await fetch(url, { signal: signal ?? null, credentials: 'omit' });
  const data = (await response.json()) as ApiResult<T> | ApiFailure;

  if (!response.ok || data.ok === false) {
    // Lösenordet har bytts/ogiltigförklarats: rensa och börja om från låsskärmen.
    // Inte för auth-endpointen själv — där är 401 ett vanligt "fel lösenord".
    if (response.status === 401 && endpoint !== 'auth') {
      clearAppKey();
      window.location.reload();
    }
    const failure = data as ApiFailure;
    throw new ApiError(
      `API ${endpoint} failed: ${failure.error}`,
      failure.error,
      response.status,
      failure,
    );
  }

  return data;
}
